import {
  AppReviewVO,
  ComponentClientContactVO,
  ComponentClientProfileVO,
  ComponentClientSearchVO,
  ComponentClientVO,
  ComponentDefaultMessageVO,
  ComponentFlowMessageVO,
  ComponentInfoChatLinksVO,
  ComponentInfoClientLinkVO,
  ComponentInfoLoadChatsVO,
  ComponentMessageLocationVO,
  ComponentMessageVO,
  ComponentRelationshipVO,
  ComponentTagVO,
  ComponentTemplateMessageVO,
  ComponentUserToTransferVO,
  ComponentUserTransferVO,
  ComponentUserVO,
  IntegratedLoginVO,
  PowerBiReportDataVO
} from '@repo/types'
import { ComponentInfoLoadVO } from '@repo/types/src/types/ComponentInfoLoadVO'
import { GenericAbortSignal } from 'axios'
import { deleteChat, deleteDefaultMessage, deleteNotificationPushUnsubscribe, deleteTag } from './delete'
import {
  getChat,
  getChats,
  getDefaultMessageById,
  getDefaultMessageByTitle,
  getDefaultMessages,
  getDepartments,
  getFlowArgs,
  getFlowPreview,
  getFlows,
  getHasAppReview,
  getKeycloakProps,
  getMessages,
  getPowerBiAccessToken,
  getTagById,
  getTagByTitle,
  getTagColors,
  getTags,
  getTemplatesByUser,
  getTransferDestinations,
  getTransferUsers,
  getTransferUsersGrouped,
  getUnreads,
  getUserConnections,
  getUserDestinations,
  getUserInfo,
  getUserMenu
} from './get'
import {
  postAppReview,
  postChatLinks,
  postClient,
  postClientContact,
  postClientLink,
  postClientProfile,
  postClientSearch,
  postContactTag,
  postDefaultMessage,
  postInfo,
  postIntegratedLogin,
  postNotificationPushSubscribe,
  postReadMessage,
  postRelationship,
  postRemoveTag,
  postSaveTag,
  postSendFlow,
  postSendLocation,
  postSendMessage,
  postSendTemplate,
  postTransferChat,
  postUpdateUser,
  postUpload,
  postUploadMedia,
  postUserAvailable
} from './post'
import { putBlockContact, putUnblockContact } from './put'
import putContactSummary from './put/putContactSummary'

export class RestChatService {
  static async messages(sessionId: number, cursor: number, signal: GenericAbortSignal) {
    return getMessages(sessionId, cursor, signal)
  }

  static async chats(input: ComponentInfoLoadChatsVO, cursor: number) {
    return getChats(input, cursor)
  }

  static async chat(input: ComponentInfoLoadVO) {
    return getChat(input)
  }

  static async sendMessage(input: ComponentMessageVO) {
    return postSendMessage(input)
  }

  static async sendLocation(input: ComponentMessageLocationVO) {
    return postSendLocation(input)
  }

  static async sendFlow(input: ComponentFlowMessageVO) {
    return postSendFlow(input)
  }

  static async userDestinations() {
    return getUserDestinations()
  }

  static async relationship(input: ComponentRelationshipVO) {
    return postRelationship(input)
  }

  static async templatesByUser(connectionKey: string) {
    return getTemplatesByUser(connectionKey)
  }

  static async sendTemplateMessage(input: ComponentTemplateMessageVO) {
    return postSendTemplate(input)
  }

  static async archiveChat(connectionKey: string, phoneNumber: string) {
    return deleteChat(connectionKey, phoneNumber)
  }

  static async readMessage(messageId: string) {
    return postReadMessage(messageId)
  }

  static async unreads(input: ComponentInfoLoadChatsVO) {
    return getUnreads(input)
  }

  static async notificationPushSubscribe(deviceToken: string) {
    return postNotificationPushSubscribe(deviceToken)
  }

  static async notificationPushUnsubscribe() {
    return deleteNotificationPushUnsubscribe()
  }

  static async userMenu() {
    return getUserMenu()
  }

  static async userInfo() {
    return getUserInfo()
  }

  static async changeUserAvailable(value: boolean) {
    return postUserAvailable(value)
  }

  static async clientProfile(input: ComponentClientProfileVO) {
    return postClientProfile(input)
  }

  static async departments() {
    return getDepartments()
  }

  static async chatLinks(input: ComponentInfoChatLinksVO) {
    return postChatLinks(input)
  }

  static async clientLink(input: ComponentInfoClientLinkVO) {
    return postClientLink(input)
  }

  static async keycloakProps(realm: string) {
    return getKeycloakProps(realm)
  }

  static async updateUser(input: ComponentUserVO) {
    return postUpdateUser(input)
  }

  static async uploadMedia(input: FormData, connectionKey: string, signal?: GenericAbortSignal) {
    return postUploadMedia(input, connectionKey, signal)
  }

  static async upload(input: FormData, connectionKey: string, phoneNumber: string) {
    return postUpload(input, connectionKey, phoneNumber)
  }

  static async info() {
    return postInfo()
  }

  static async defaultMessages(filter: string) {
    return getDefaultMessages(filter)
  }

  static async saveDefaultMessage(input: ComponentDefaultMessageVO) {
    return postDefaultMessage(input)
  }

  static async deleteDefaultMessageById(id: number) {
    return deleteDefaultMessage(id)
  }

  static async transferUsers(input: ComponentUserToTransferVO) {
    return getTransferUsers(input)
  }

  static async transferUsersGrouped(input: ComponentUserToTransferVO, chatUserId: string, filter?: string) {
    return getTransferUsersGrouped(input, chatUserId, filter)
  }

  static async transferDestinations(connectionKey: string, isTakeChat: boolean) {
    return getTransferDestinations(connectionKey, isTakeChat)
  }

  static async transferChat(input: ComponentUserTransferVO) {
    return postTransferChat(input)
  }

  static async powerBiAccessToken(input: PowerBiReportDataVO) {
    return getPowerBiAccessToken(input)
  }

  static async saveClient(input: ComponentClientVO) {
    return postClient(input)
  }

  static async saveClientContact(input: ComponentClientContactVO) {
    return postClientContact(input)
  }

  static async clientSearch(input: ComponentClientSearchVO, page: number, offset: number, signal: GenericAbortSignal) {
    return postClientSearch(input, page, offset, signal)
  }

  static async defaultMessageById(id: string) {
    return getDefaultMessageById(id)
  }

  static async defaultMessageByTitle(title: string) {
    return getDefaultMessageByTitle(title)
  }

  static async userConnections() {
    return getUserConnections()
  }

  static async flowPreview(connectionKey: string, flowId: string) {
    return getFlowPreview(connectionKey, flowId)
  }

  static async flowArgs(connectionKey: string, flowId: string) {
    return getFlowArgs(connectionKey, flowId)
  }

  static async saveAppReview(input: AppReviewVO) {
    return postAppReview(input)
  }

  static async flows(connectionKey: string) {
    return getFlows(connectionKey)
  }

  static async hasAppReview() {
    return getHasAppReview()
  }

  static async integratedLogin(loginVO: IntegratedLoginVO) {
    return postIntegratedLogin(loginVO)
  }

  static async blockContact(connectionKey: string, phoneNumber: string) {
    return putBlockContact(connectionKey, phoneNumber)
  }

  static async unblockContact(connectionKey: string, phoneNumber: string) {
    return putUnblockContact(connectionKey, phoneNumber)
  }

  static async contactSummary(connectionKey: string, phoneNumber: string) {
    return putContactSummary(connectionKey, phoneNumber)
  }

  static async tags(filter?: string) {
    return getTags(filter)
  }

  static async tagById(id: string) {
    return getTagById(id)
  }

  static async tagByTitle(title: string) {
    return getTagByTitle(title)
  }

  static async saveTag(input: ComponentTagVO) {
    return postSaveTag(input)
  }

  static async deleteTag(id: number) {
    return deleteTag(id)
  }

  static async saveContactTag(input: ComponentTagVO, connectionKey: string, phoneNumber: string) {
    return postContactTag(input, connectionKey, phoneNumber)
  }

  static async removeContactTag(input: ComponentTagVO, connectionKey: string, phoneNumber: string) {
    return postRemoveTag(input, connectionKey, phoneNumber)
  }

  static async chatTagColors() {
    return getTagColors()
  }
}
